<template>
  <AModal
    :model-value="modelValue"
    title="시리즈 관리"
    sub="교재를 묶는 시리즈 마스터입니다. 교재별 권차는 교재 편집 화면에서 지정합니다."
    size="xl"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <div class="ts-modal__bar">
      <div class="ts-modal__count">총 <strong>{{ seriesList.length }}</strong>개 시리즈</div>
      <div class="ts-modal__bar-actions">
        <input
          v-model.trim="keyword"
          class="p-input ts-modal__search"
          type="text"
          placeholder="시리즈 이름 검색"
        />
        <ABtn size="sm" kind="primary" icon="add" @click="openAdd">시리즈 추가</ABtn>
      </div>
    </div>

    <!-- 추가 / 수정 겸용 인라인 폼 -->
    <div v-if="form" class="ts-modal__form">
      <div class="ts-modal__form-title">
        {{ form.mode === 'add' ? '시리즈 추가' : `시리즈 수정 — ${original.name}` }}
      </div>
      <div class="ts-modal__form-grid">
        <label class="ts-modal__field ts-modal__field--wide">
          <span>이름 <b class="ts-modal__req">*</b></span>
          <input
            ref="nameInput"
            v-model.trim="form.name"
            class="p-input"
            :class="{ 'is-invalid': isDuplicateName }"
            type="text"
            maxlength="100"
            placeholder="예: Anytime Korean"
          />
          <small v-if="isDuplicateName" class="ts-modal__err">
            같은 이름의 시리즈가 이미 있습니다. (대소문자 구분 없음)
          </small>
        </label>
        <label class="ts-modal__field ts-modal__field--wide">
          <span>설명 <small>(선택)</small></span>
          <input v-model.trim="form.description" class="p-input" type="text" placeholder="시리즈 설명" />
        </label>
        <label class="ts-modal__field">
          <span>노출순서 <small>(선택)</small></span>
          <input v-model="form.sort_order" class="p-input" type="number" placeholder="비우면 맨 뒤" />
        </label>
      </div>
      <div class="ts-modal__form-actions">
        <ABtn size="sm" kind="ghost" @click="cancelForm">취소</ABtn>
        <ABtn size="sm" kind="primary" :disabled="!canSave" :loading="saving" @click="save">
          {{ form.mode === 'add' ? '추가' : '수정 저장' }}
        </ABtn>
      </div>
    </div>

    <div class="ts-modal__table">
      <ATable :columns="columns" :rows="filteredList" row-key="idx" striped bordered>
        <template #cell:_no="{ index }">{{ index + 1 }}</template>

        <template #cell:name="{ row }">
          <span class="ts-modal__name">{{ row.name }}</span>
          <span v-if="isSelected(row.idx)" class="ts-modal__badge">선택중</span>
        </template>

        <template #cell:description="{ row }">
          <span class="ts-modal__desc">{{ row.description || '-' }}</span>
        </template>

        <template #cell:textbook_count="{ row }">
          <span :class="['ts-modal__cnt', { 'is-zero': !row.textbook_count }]">{{ row.textbook_count }}권</span>
        </template>

        <template #cell:created_at="{ row }">
          <span class="ts-modal__date">{{ formatDate(row.created_at) }}</span>
        </template>

        <template #cell:_actions="{ row }">
          <div class="ts-modal__row-actions">
            <ABtn size="sm" kind="ghost" icon="edit" icon-only title="수정" @click="openEdit(row)" />
            <span
              class="ts-modal__del-wrap"
              :title="row.textbook_count > 0
                ? `소속 교재 ${row.textbook_count}권을 먼저 해제해야 삭제할 수 있습니다`
                : '삭제'"
            >
              <ABtn
                size="sm"
                kind="danger"
                icon="delete"
                icon-only
                :disabled="row.textbook_count > 0"
                @click="remove(row)"
              />
            </span>
          </div>
        </template>

        <template #empty>
          {{ keyword ? '검색 결과가 없습니다.' : '등록된 시리즈가 없습니다.' }}
        </template>
      </ATable>
    </div>

    <template #foot>
      <ABtn kind="ghost" @click="$emit('update:modelValue', false)">닫기</ABtn>
    </template>
  </AModal>
</template>

<script>
import swal from "sweetalert2";
import AModal from "@/components/Pineapple/AModal.vue";
import ABtn from "@/components/Pineapple/ABtn.vue";
import ATable from "@/components/Pineapple/ATable.vue";
import { apiErrorMessage, apiErrorStatus } from "@/utils/apiError";

// 이름 중복 판정 기준. 서버도 동일하게 앞뒤 공백 제거 + 대소문자 무시로 판정합니다.
const normalizeName = (v) => (v || "").trim().toLowerCase();

export default {
  name: "TextbookSeriesModal",
  components: { AModal, ABtn, ATable },
  props: {
    modelValue: { type: Boolean, default: false },
    // 교재 편집 폼에서 현재 선택 중인 시리즈. 삭제 시 부모에게 알려주기 위해 참조합니다.
    selectedIdx: { type: [Number, String], default: "" },
  },
  emits: ["update:modelValue", "changed"],
  data() {
    return {
      seriesList: [],
      keyword: "",
      form: null, // null=목록만, { mode:'add'|'edit', idx, name, description, sort_order }
      original: null, // 수정 모드에서 변경분 판정용 원본
      saving: false,
      columns: [
        { key: "_no", label: "순서", align: "center", width: 60 },
        { key: "name", label: "시리즈명", width: 220 },
        { key: "description", label: "설명" },
        { key: "textbook_count", label: "교재 수", align: "center", width: 90 },
        { key: "created_at", label: "등록일", align: "center", width: 110 },
        { key: "_actions", label: "관리", align: "right", width: 90 },
      ],
    };
  },
  computed: {
    filteredList() {
      const k = normalizeName(this.keyword);
      if (!k) return this.seriesList;
      return this.seriesList.filter(
        (s) => normalizeName(s.name).includes(k) || normalizeName(s.description).includes(k)
      );
    },
    // 서버 409를 미리 막기 위한 사전 검사 (자기 자신은 제외)
    isDuplicateName() {
      if (!this.form || !this.form.name) return false;
      const target = normalizeName(this.form.name);
      return this.seriesList.some(
        (s) => normalizeName(s.name) === target && s.idx !== this.form.idx
      );
    },
    // 수정 모드에서 변경분이 없으면 요청 자체를 보내지 않습니다 (400 "수정할 내용이 없습니다" 예방)
    isDirty() {
      if (!this.form || this.form.mode === "add") return true;
      const o = this.original;
      return (
        this.form.name !== o.name ||
        this.form.description !== o.description ||
        String(this.form.sort_order) !== String(o.sort_order)
      );
    },
    canSave() {
      if (!this.form) return false;
      return !!this.form.name && !this.isDuplicateName && this.isDirty;
    },
  },
  watch: {
    modelValue(v) {
      // 열 때마다 최신본 재조회. 다른 관리자가 그 사이 바꿨을 수 있어 캐시를 쓰지 않습니다.
      if (v) {
        this.keyword = "";
        this.cancelForm();
        this.fetchSeries();
      }
    },
  },
  methods: {
    async fetchSeries() {
      try {
        const res = await this.$axios({
          method: "get",
          url: "/textbook/series",
          loadingBar: true,
        });
        if (res.status === 200) this.seriesList = res.data || [];
      } catch (e) {
        console.error("시리즈 목록 조회 실패:", e);
        swal.fire("오류", apiErrorMessage(e, "시리즈 목록을 불러오지 못했습니다."), "error");
      }
    },
    formatDate(v) {
      return v ? String(v).substring(0, 10) : "-";
    },
    // 편집 중인 교재가 쓰고 있는 시리즈를 표시해, 어떤 행을 건드리는지 알 수 있게 합니다.
    isSelected(idx) {
      return this.selectedIdx !== "" && Number(this.selectedIdx) === Number(idx);
    },
    openAdd() {
      this.form = { mode: "add", idx: null, name: "", description: "", sort_order: "" };
      this.original = null;
      this.$nextTick(() => this.$refs.nameInput?.focus());
    },
    openEdit(row) {
      const snapshot = {
        idx: row.idx,
        name: row.name || "",
        description: row.description || "",
        sort_order: row.sort_order ?? "",
      };
      this.original = snapshot;
      this.form = { mode: "edit", ...snapshot };
      this.$nextTick(() => this.$refs.nameInput?.focus());
    },
    cancelForm() {
      this.form = null;
      this.original = null;
    },
    async save() {
      if (!this.canSave || this.saving) return;
      const f = this.form;
      this.saving = true;

      try {
        let createdIdx = null;

        if (f.mode === "add") {
          const data = { name: f.name };
          if (f.description) data.description = f.description;
          // 비워두면 서버가 MAX+1로 맨 뒤에 자동 배치합니다.
          if (f.sort_order !== "" && f.sort_order != null) data.sort_order = Number(f.sort_order);

          const res = await this.$axios({
            method: "post",
            url: "/textbook/series",
            data,
            loadingBar: true,
          });
          // 응답이 생성된 객체가 아니라 true 인 경우도 있어, 아래에서 이름으로 한 번 더 찾습니다.
          createdIdx = res.data?.idx ?? null;
        } else {
          // 변경된 필드만 전송
          const data = {};
          if (f.name !== this.original.name) data.name = f.name;
          if (f.description !== this.original.description) data.description = f.description;
          if (String(f.sort_order) !== String(this.original.sort_order)) {
            data.sort_order = f.sort_order === "" || f.sort_order == null ? null : Number(f.sort_order);
          }

          await this.$axios({
            method: "put",
            url: `/textbook/series/${f.idx}`,
            data,
            loadingBar: true,
          });
        }

        this.cancelForm();
        await this.fetchSeries();

        // 이름은 유일하므로 방금 만든 시리즈를 확실히 찾을 수 있습니다.
        if (f.mode === "add" && createdIdx == null) {
          const created = this.seriesList.find((s) => normalizeName(s.name) === normalizeName(f.name));
          createdIdx = created?.idx ?? null;
        }

        this.$emit("changed", { list: this.seriesList, createdIdx, deletedIdx: null });
        this.$notifyVue({
          type: "success",
          title: f.mode === "add" ? "시리즈를 추가했습니다." : "시리즈를 수정했습니다.",
        });
      } catch (e) {
        this.showSeriesError(e);
      } finally {
        this.saving = false;
      }
    },
    async remove(row) {
      // 버튼이 비활성이라 정상 흐름에서는 도달하지 않지만, 방어적으로 한 번 더 막습니다.
      if (row.textbook_count > 0) return;

      // 시리즈 이름은 관리자가 입력하는 값이므로 html 옵션에 넣지 않습니다(관리자 간 stored-XSS 방지).
      const ok = await swal.fire({
        title: "시리즈 삭제",
        text: `'${row.name}' 시리즈를 삭제하시겠습니까?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "삭제",
        cancelButtonText: "취소",
        confirmButtonColor: "#d33",
      });
      if (!ok.isConfirmed) return;

      try {
        await this.$axios({
          method: "delete",
          url: `/textbook/series/${row.idx}`,
          loadingBar: true,
        });
        if (this.form && this.form.idx === row.idx) this.cancelForm();
        await this.fetchSeries();
        this.$emit("changed", { list: this.seriesList, createdIdx: null, deletedIdx: row.idx });
        this.$notifyVue({ type: "success", title: "시리즈를 삭제했습니다." });
      } catch (e) {
        this.showSeriesError(e, row);
      }
    },
    // 소속 교재 목록을 새 탭으로. 편집 중인 교재 폼을 보호하기 위해 현재 탭을 옮기지 않습니다.
    openSeriesTextbooks(idx) {
      const url = this.$router.resolve({
        path: "/contents/textbooks",
        query: { series_idx: idx },
      }).href;
      window.open(url, "_blank");
    },
    async showSeriesError(e, row) {
      const status = apiErrorStatus(e);
      const detail = apiErrorMessage(e, "처리 중 오류가 발생했습니다.");
      console.error("시리즈 API 오류:", status, e);

      // 삭제 차단 409는 서버 문구가 이미 다음 행동을 안내하고 있어 그대로 노출합니다.
      if (status === 409 && row) {
        const r = await swal.fire({
          icon: "warning",
          title: "삭제할 수 없습니다",
          text: detail,
          showCancelButton: true,
          confirmButtonText: "소속 교재 보기",
          cancelButtonText: "닫기",
        });
        if (r.isConfirmed) this.openSeriesTextbooks(row.idx);
        await this.fetchSeries();
        return;
      }

      const map = {
        400: detail,
        401: "관리자만 시리즈를 등록·수정·삭제할 수 있습니다.",
        404: "이미 삭제된 시리즈입니다.",
        409: "같은 이름의 시리즈가 이미 있습니다. (대소문자 구분 없음)",
        422: detail,
      };
      swal.fire("오류", map[status] || detail, "error");

      // 404/409는 다른 관리자의 변경으로 목록이 낡았다는 신호이므로 재조회합니다.
      if (status === 404 || status === 409) await this.fetchSeries();
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.ts-modal__bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 10px;
}

.ts-modal__bar-actions {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.ts-modal__search {
  width: 220px;
  height: 28px;
  font-size: 12px;
}

.ts-modal__count {
  font-size: 13px;
  color: $p-ink-mid;

  strong { color: $p-ink; font-weight: 800; }
}

.ts-modal__form {
  margin-bottom: 14px;
  padding: 14px;
  border: 1px solid $p-line;
  border-radius: $p-radius-sm;
  background: $p-panel-alt;

  &-title { font-size: 13px; font-weight: 800; color: $p-ink; margin-bottom: 10px; }

  &-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 10px;
  }

  &-actions {
    display: flex;
    justify-content: flex-end;
    gap: 6px;
    margin-top: 12px;
  }
}

.ts-modal__field {
  display: flex;
  flex-direction: column;
  gap: 4px;

  span { font-size: 12px; font-weight: 700; color: $p-ink-mid; }
  small { font-weight: 400; color: $p-ink-soft; }

  &--wide { grid-column: span 2; }
}

.ts-modal__req { color: $p-err; }

.ts-modal__err { color: $p-err; font-size: 11px; }

.ts-modal__name { font-weight: 700; color: $p-ink; }

.ts-modal__badge {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 6px;
  border-radius: 4px;
  background: $p-point;
  font-size: 10px;
  font-weight: 800;
  color: $p-ink;
}

.ts-modal__desc { color: $p-ink-mid; }

.ts-modal__cnt {
  font-weight: 700;
  color: $p-ink;

  &.is-zero { color: $p-ink-soft; font-weight: 400; }
}

.ts-modal__date { color: $p-ink-soft; font-size: 12px; }

.ts-modal__row-actions {
  display: inline-flex;
  gap: 4px;
}

// disabled 버튼은 포인터 이벤트가 죽어 툴팁이 뜨지 않으므로 래퍼에 title을 겁니다.
.ts-modal__del-wrap { display: inline-flex; }

// 목록 세로 스크롤 시 헤더 상단 고정
.ts-modal__table {
  :deep(.a-table-wrap) {
    max-height: 55vh;
    overflow-y: auto;
  }

  :deep(.a-table__th) {
    position: sticky;
    top: 0;
    z-index: 2;
    box-shadow: inset 0 -1px 0 $p-line;
  }
}
</style>
